import type { MatchTier } from './effects';
import type { Piece } from './types';

/**
 * Relics are run-long modifiers, granted between fights. Every field here is
 * plain data: the combat and resolve layers read them at the points they
 * already own. No closures, no hook registry — which keeps relics trivially
 * testable and keeps this module free of any import from combat.
 */
export interface RelicDef {
  readonly id: string;
  readonly name: string;
  readonly text: string;

  /** Flat bonus added to each wave's row burst. */
  readonly rowBurstBonus?: number;

  /** Damage dealt per Stone gem destroyed by a row clear, each wave. */
  readonly stoneRowDamage?: number;

  /** Block granted for any wave that reaches this tier or higher. */
  readonly blockOnTier?: { readonly tier: MatchTier; readonly block: number };

  /** Damage per capped column, replacing the base 8. */
  readonly capDamagePerColumn?: number;

  /** Added to the cascade multiplier cap. 1 takes ×3 to ×4. */
  readonly cascadeCapBonus?: number;

  /** Extra cards drawn per turn. */
  readonly drawBonus?: number;

  /** Extra Energy each turn. */
  readonly energyBonus?: number;

  /** Pre-placed into the well at the start of every combat. */
  readonly startPiece?: Piece;
}

export const RUNE_OF_THE_BULWARK: RelicDef = {
  id: 'rune_of_the_bulwark',
  name: 'Rune of the Bulwark',
  text: 'On a 5+ match, gain 6 Block.',
  blockOnTier: { tier: 'surge', block: 6 },
};

export const LEAD_WEIGHT: RelicDef = {
  id: 'lead_weight',
  name: 'Lead Weight',
  text: 'Row clears deal +4 damage.',
  rowBurstBonus: 4,
};

export const MASON_TROWEL: RelicDef = {
  id: 'masons_trowel',
  name: "Mason's Trowel",
  text: 'Stone destroyed by row clears deals 4 damage each.',
  stoneRowDamage: 4,
};

export const OBSIDIAN_ANCHOR: RelicDef = {
  id: 'obsidian_anchor',
  name: 'Obsidian Anchor',
  text: 'Capped columns deal 4 damage instead of 8.',
  capDamagePerColumn: 4,
};

export const CHAIN_AMULET: RelicDef = {
  id: 'chain_amulet',
  name: 'Chain Amulet',
  text: 'Cascade multiplier cap rises from x3 to x4.',
  cascadeCapBonus: 1,
};

export const BOTTOMLESS_BAG: RelicDef = {
  id: 'bottomless_bag',
  name: 'Bottomless Bag',
  text: 'Draw 6 cards each turn instead of 5.',
  drawBonus: 1,
};

export const EMBER_CORE: RelicDef = {
  id: 'ember_core',
  name: 'Ember Core',
  text: 'Start each turn with 1 extra Energy.',
  energyBonus: 1,
};

const PRISM_START: Piece = {
  cells: [[0, 0], [0, 1], [1, 0], [1, 1]],
  kinds: ['volt', 'volt', 'volt', 'volt'],
};

export const CRACKED_PRISM: RelicDef = {
  id: 'cracked_prism',
  name: 'Cracked Prism',
  text: 'Start each combat with a 2x2 Volt piece already in the well.',
  startPiece: PRISM_START,
};

export const ALL_RELICS: readonly RelicDef[] = [
  RUNE_OF_THE_BULWARK,
  LEAD_WEIGHT,
  MASON_TROWEL,
  OBSIDIAN_ANCHOR,
  CHAIN_AMULET,
  BOTTOMLESS_BAG,
  EMBER_CORE,
  CRACKED_PRISM,
];

/** Sum a numeric relic field across everything held. */
export function relicSum(
  relics: readonly RelicDef[],
  field: 'rowBurstBonus' | 'stoneRowDamage' | 'cascadeCapBonus',
): number {
  let total = 0;
  for (const relic of relics) total += relic[field] ?? 0;
  return total;
}

/**
 * Cap damage is an override, not a rider — Obsidian Anchor replaces the base
 * outright rather than adding to it, so the first relic that names a value
 * wins.
 */
export function capDamageFor(relics: readonly RelicDef[], base: number): number {
  for (const relic of relics) {
    if (relic.capDamagePerColumn !== undefined) return relic.capDamagePerColumn;
  }
  return base;
}

/** Highest block-on-tier entry at or below the given tier, summed. */
export function blockForTier(relics: readonly RelicDef[], tier: MatchTier): number {
  const order: MatchTier[] = ['base', 'charged', 'surge', 'aether'];
  const reached = order.indexOf(tier);
  let total = 0;
  for (const relic of relics) {
    const hook = relic.blockOnTier;
    if (!hook) continue;
    if (order.indexOf(hook.tier) <= reached) total += hook.block;
  }
  return total;
}
